/**
 * Normaliseert de (helaas niet-uniforme) validatie-fout-envelope van de API naar
 * per-veld foutmeldingen, zodat forms terechte invoerfouten INLINE kunnen tonen
 * i.p.v. een generieke "Validatiefout"/"Ongeldige invoer"-toast.
 *
 * De API geeft validatiefouten op drie manieren terug (zie
 * `apps/api/src/plugins/error-handler.ts` + `lib/validation-error.ts` + lokale
 * `badRequest`-helpers):
 *  - `issues: [{ field, message, code }]`  (thrown ZodError + sendValidationError)
 *  - `details: { fieldErrors: Record<field, string[]>, formErrors: [] }` (sommige `.flatten()`)
 *  - `details: Record<field, string[]>`     (andere `.flatten().fieldErrors`)
 * Deze helper accepteert alle drie.
 */

export interface ApiValidationIssue {
  field: string
  message: string
  code?: string
}

export interface ApiValidationError {
  issues?: ApiValidationIssue[]
  details?: unknown
}

function extractFieldErrors(details: unknown): Record<string, string[]> {
  if (!details || typeof details !== 'object') return {}
  const obj = details as Record<string, unknown>
  // Vorm A: volledige flatten() → { formErrors, fieldErrors }
  if (obj.fieldErrors && typeof obj.fieldErrors === 'object') {
    return obj.fieldErrors as Record<string, string[]>
  }
  // Vorm B: kale fieldErrors → { veld: string[] }
  const out: Record<string, string[]> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'formErrors') continue
    if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      out[key] = value as string[]
    }
  }
  return out
}

/** Eerste foutmelding per veld, uit `issues[]` (voorkeur) of `details`. */
export function issuesToFieldErrors(err: ApiValidationError | null | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  if (!err) return out
  if (Array.isArray(err.issues)) {
    for (const issue of err.issues) {
      if (issue.field && !out[issue.field]) out[issue.field] = issue.message
    }
  }
  for (const [field, msgs] of Object.entries(extractFieldErrors(err.details))) {
    if (!out[field] && msgs.length > 0) out[field] = msgs[0]
  }
  return out
}

/**
 * Korte, vriendelijke samenvatting voor een toast wanneer een form (nog) geen
 * per-veld-rendering heeft. Geeft `null` als er geen veld-issues zijn (dan is het
 * geen veld-validatiefout en mag de caller zijn eigen melding tonen).
 * `labels` mapt veldnamen naar leesbare labels (bv. `naam` → "Naam").
 */
export function summarizeValidationIssues(
  err: ApiValidationError | null | undefined,
  labels?: Record<string, string>,
): string | null {
  const fields = Object.keys(issuesToFieldErrors(err))
  if (fields.length === 0) return null
  const names = fields.map((f) => labels?.[f] ?? f)
  return `Controleer de gemarkeerde velden: ${names.join(', ')}`
}
